import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import moment from "moment";
import {
  SectionHeader,
  Card,
  Tag,
  Button,
  EmptyState,
  Skeleton,
  Input,
} from "../../../design-system";
import {
  IconFlame,
  IconUsers,
  IconCompass,
  IconChevronDown,
} from "../../../design-system/icons/icons";
import {
  OutreachLead,
  LeadStage,
  StageDef,
  StageConfig,
  StageTone,
  STAGE_TONES,
  OutreachDraft,
  OutreachRunSummary,
} from "../../../types/OutreachMetrics";
import {
  listOutreachAccounts,
  OutreachAccountSummary,
  getOutreachLeads,
  getOutreachLead,
  updateOutreachLead,
  getOutreachStages,
  setOutreachStages,
  listOutreachDrafts,
} from "../../../database/outreach/outreachApi";
import {
  SectionSpacing,
  StatGrid,
  StatCard,
  Funnel,
  computeBottleneck,
  Callout,
  CalloutLabel,
  CalloutValue,
  CalloutHint,
} from "./components/ui";
import LeadBoard from "./components/LeadBoard";
import ReviewQueue from "./components/ReviewQueue";
import DailyAnalysis from "./components/DailyAnalysis";
import WorkQueue, { workQueueTotal } from "./components/WorkQueue";
import RailTable from "./components/RailTable";
import RailDetail from "./components/RailDetail";
import HistoryPanel from "./components/HistoryPanel";
import Modal from "./components/Modal";
import { useOutreachAnalysis } from "./components/analysisData";
import { buildQueueModel, opsSummaryOf } from "./components/queueModel";
import {
  channelLabel,
  dueOf,
  dueSortKey,
  contactLink,
  isAutomatedStep,
  buildStageModel,
  StageModel,
  TONE_DOT,
} from "./components/leadUtils";

// ─── styled ───────────────────────────────────────────────────────────────────

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-bottom: var(--space-5);
`;

const UpdatedText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-right: auto;
`;

const TabBar = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-6);
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    color: var(--color-text);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--color-surface);
      color: var(--color-text);
      font-weight: var(--font-weight-semibold);
      box-shadow: var(--shadow-xs);
    `}
`;

// Count pill shown inside a sub-tab — e.g. how many follow-ups are waiting on
// Today. Tone escalates to danger when anything is overdue.
const TabBadge = styled.span<{ $tone: "danger" | "accent" | "neutral" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  ${(p) =>
    p.$tone === "danger"
      ? css`
          background: var(--color-danger);
          color: var(--color-text-inverse);
        `
      : p.$tone === "accent"
      ? css`
          background: var(--color-accent);
          color: var(--color-text-inverse);
        `
      : css`
          background: var(--color-surface-inset);
          color: var(--color-text-secondary);
        `}
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-4);
  background: var(--color-danger-subtle, var(--color-surface-inset));
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

// Segmented channel filter (All / LinkedIn / Instagram / …). Shared by the Today
// queue and the Leads board so the channel in focus follows you across tabs.
const ChannelChips = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const ChannelChip = styled.button<{ $active: boolean }>`
  height: 28px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard);

  ${(p) =>
    p.$active
      ? css`
          background: var(--color-accent);
          color: var(--color-text-inverse);
          border: 1px solid var(--color-accent);
        `
      : css`
          background: var(--color-surface);
          color: var(--color-text-secondary);
          border: 1px solid var(--color-border);

          &:hover {
            color: var(--color-text);
            border-color: var(--color-border-strong, var(--color-accent));
          }
        `}
`;

const SelectShell = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  cursor: pointer;

  span {
    color: var(--color-text-tertiary);
    font-size: var(--font-size-xs);
  }
`;

const NativeSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  cursor: pointer;
`;

const LeadList = styled.div`
  display: flex;
  flex-direction: column;
`;

// Expander at the bottom of a paged queue section. The queue never renders more
// than a page at a time; the rest stays behind this button.
const ShowMoreBar = styled.button`
  width: 100%;
  margin-top: var(--space-2);
  height: 34px;
  border: 1px dashed var(--color-border);
  background: transparent;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;

  &:hover {
    color: var(--color-text);
    border-color: var(--color-accent);
  }
`;

const LeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2);
  border-bottom: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle);
  }
  &:last-child {
    border-bottom: none;
  }
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-shrink: 0;
`;

// On the Today tab acting on a lead is the whole point, so the quick actions are
// always visible rather than hover-revealed.
const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LeadMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const LeadTitleLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
`;

const LeadName = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const LeadMeta = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

// Clickable profile/email link inside a row's meta line. Opens the platform in
// a new tab without triggering the row's drawer.
const MetaLink = styled.a`
  color: var(--color-accent-text, var(--color-text-secondary));
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const LeadAction = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

const NextActionText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  max-width: 280px;
`;

const DueBadge = styled.span<{ $tone: "overdue" | "today" | "future" | "none" }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 2px var(--space-2);
  border-radius: var(--radius-full);
  ${(p) =>
    p.$tone === "overdue"
      ? css`
          color: var(--color-danger);
          background: var(--color-danger-subtle, var(--color-surface-inset));
        `
      : p.$tone === "today"
      ? css`
          color: var(--color-warning);
          background: var(--color-warning-subtle, var(--color-surface-inset));
        `
      : p.$tone === "future"
      ? css`
          color: var(--color-text-secondary);
          background: var(--color-surface-inset);
        `
      : css`
          color: var(--color-text-tertiary);
          background: var(--color-surface-inset);
        `}
`;

// Today header: the summary line on the left, the channel filter on the right.
const HopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border-subtle);
`;

const Hop = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const HopValue = styled.span`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin-left: var(--space-2);
  font-variant-numeric: tabular-nums;
`;

// The heading above each half of the expanded follow-up list. The old summary
// bar and stage chip row are gone: their counts are the queue's own, and stage
// filtering is a Leads board job.
const QueueGroupLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  margin: var(--space-4) 0 var(--space-2);
`;

const SnoozeWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  min-width: 148px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  padding: 4px;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.button`
  text-align: left;
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  cursor: pointer;

  &:hover {
    background: var(--color-surface-subtle);
  }
`;

const MenuDateInput = styled.input`
  margin: 4px;
  height: 34px;
  padding: 0 var(--space-2);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

// Lead detail drawer (slides in from the right over a dimmed backdrop).
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: flex-end;
`;

const Drawer = styled.div`
  width: min(460px, 100%);
  height: 100%;
  background: var(--color-surface);
  border-left: 1px solid var(--color-border);
  box-shadow: var(--shadow-lg);
  overflow-y: auto;
  padding: var(--space-6);
`;

const DrawerTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
`;

const DrawerTitle = styled.h2`
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0 0 var(--space-2);
`;

const ChipRow = styled.div`
  display: inline-flex;
  gap: var(--space-2);
  flex-wrap: wrap;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--radius-sm);

  &:hover {
    color: var(--color-text);
    background: var(--color-surface-subtle);
  }
`;

const DetailSection = styled.div`
  margin-top: var(--space-5);
`;

const DetailLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  margin-bottom: var(--space-2);
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-2) var(--space-4);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
`;

const FieldKey = styled.div`
  color: var(--color-text-tertiary);
`;

const FieldVal = styled.div`
  color: var(--color-text);
  word-break: break-word;
`;

// Copy drafted message text to the clipboard (best-effort; no-op if blocked).
const copyText = (t: string) => {
  navigator.clipboard?.writeText(t).catch(() => {});
};

const MsgCard = styled.div`
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md, 8px);
  padding: var(--space-3);
  margin-top: var(--space-2);
`;

const MsgCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  margin-bottom: var(--space-1);
`;

const MsgCardLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const HdrLink = styled.a`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent, var(--color-text-secondary));
  text-decoration: none;
  align-self: center;
  &:hover {
    text-decoration: underline;
  }
`;

const Para = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
  white-space: pre-wrap;
`;

const TouchItem = styled.div`
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: none;
  }
`;

const TouchTime = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-bottom: 2px;
`;

const ButtonWrap = styled.div`
  margin-top: var(--space-2);
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
`;

const DateInput = styled.input`
  height: 36px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const ActionStatus = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: var(--space-2);
`;

const ActionError = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
  margin-top: var(--space-2);
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const MsgRow = styled.div<{ $me: boolean }>`
  display: flex;
  justify-content: ${(p) => (p.$me ? "flex-end" : "flex-start")};
`;

const Bubble = styled.div<{ $me: boolean }>`
  max-width: 82%;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-snug);
  white-space: pre-wrap;
  word-break: break-word;
  ${(p) =>
    p.$me
      ? css`
          background: var(--color-accent);
          color: var(--color-text-inverse);
        `
      : css`
          background: var(--color-surface-inset);
          color: var(--color-text);
        `}
`;

const MsgMeta = styled.div<{ $me: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
  text-align: ${(p) => (p.$me ? "right" : "left")};
`;

type TabDef = { id: number; label: string; icon?: React.ReactNode };
// Two tabs, because the screen answers two questions and not twelve.
//
// Today: what needs a person now, and whether the rails delivered what they
// were allowed to. Leads: look one up. Everything that used to be its own tab
// (ops lanes, the drafts queue, the pipeline stats, the per channel workers)
// opens from the number it explains. See feature-research/outreach-screen-revamp.
const TAB_TODAY = 0;
const TAB_LEADS = 1;
const TABS: TabDef[] = [
  { id: TAB_TODAY, label: "Today", icon: <IconFlame size={14} /> },
  { id: TAB_LEADS, label: "Leads", icon: <IconUsers size={14} /> },
];

// ─── Ops home (the four-worker pipeline) ──────────────────────────────────────
// Source → Write → Send → Reply. Each lane is one stage of the daily run,
// shown with what it produced and what needs a human. All figures derive from
// the leads + drafts + last-run already loaded — no extra backend.

const OpsCaption = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-3);
`;

const LaneGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: var(--space-3);
`;

const Lane = styled.button`
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    box-shadow: var(--shadow-sm);
    border-color: var(--color-accent);
  }
`;

const LaneStep = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-tertiary);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

// The lane's state reads off this dot and the tone-colored count, not off the
// card edge — same signal, without turning every lane into a colored tab.
const LaneDot = styled.span<{ $tone: "danger" | "accent" | "muted" }>`
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  flex-shrink: 0;
  background: ${(p) =>
    p.$tone === "danger"
      ? "var(--color-danger)"
      : p.$tone === "accent"
      ? "var(--color-accent)"
      : "var(--color-text-tertiary)"};
`;

const LaneName = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const LaneBig = styled.div<{ $tone: "danger" | "accent" | "muted" }>`
  font-family: var(--font-display);
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  color: ${(p) =>
    p.$tone === "danger"
      ? "var(--color-danger)"
      : p.$tone === "accent"
      ? "var(--color-accent-text)"
      : "var(--color-text-tertiary)"};
`;

const LaneBigLabel = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  margin-left: var(--space-2);
`;

const LaneSub = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
`;

// Channel filter as a row of pills. Hidden when there's only one channel, since
// there's then nothing to filter between.
const ChannelFilter: React.FC<{
  channels: string[];
  value: string;
  onChange: (value: string) => void;
}> = ({ channels, value, onChange }) => {
  if (channels.length <= 1) return null;
  return (
    <ChannelChips role="group" aria-label="Filter by channel">
      <ChannelChip
        type="button"
        $active={value === "all"}
        onClick={() => onChange("all")}
      >
        All
      </ChannelChip>
      {channels.map((c) => (
        <ChannelChip
          key={c}
          type="button"
          $active={value === c}
          onClick={() => onChange(c)}
        >
          {channelLabel(c)}
        </ChannelChip>
      ))}
    </ChannelChips>
  );
};

// Inline snooze dropdown: presets (tomorrow / 3 days / 1 week) plus a date
// picker. Calls back with a YYYY-MM-DD date and a human label for the touch note.
const SNOOZE_PRESETS: { label: string; days: number; note: string }[] = [
  { label: "Tomorrow", days: 1, note: "Snoozed to tomorrow" },
  { label: "3 days", days: 3, note: "Snoozed 3 days" },
  { label: "1 week", days: 7, note: "Snoozed 1 week" },
];

const SnoozeMenu: React.FC<{
  onPick: (dateStr: string, note: string) => void;
}> = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setPicking(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const close = () => {
    setOpen(false);
    setPicking(false);
  };

  return (
    <SnoozeWrap
      ref={ref}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        Snooze ▾
      </Button>
      {open && (
        <Menu>
          {SNOOZE_PRESETS.map((p) => (
            <MenuItem
              key={p.days}
              onClick={() => {
                onPick(moment().add(p.days, "days").format("YYYY-MM-DD"), p.note);
                close();
              }}
            >
              {p.label}
            </MenuItem>
          ))}
          {picking ? (
            <MenuDateInput
              type="date"
              autoFocus
              min={moment().format("YYYY-MM-DD")}
              onChange={(e) => {
                if (e.target.value) {
                  onPick(
                    e.target.value,
                    `Rescheduled to ${moment(e.target.value).format("MMM D")}`
                  );
                  close();
                }
              }}
            />
          ) : (
            <MenuItem onClick={() => setPicking(true)}>Pick a date…</MenuItem>
          )}
        </Menu>
      )}
    </SnoozeWrap>
  );
};

// One lead row: name + channel/stage chips on the left, next action + due on the
// right. Quick actions (snooze / advance / done) sit on the row so a follow-up
// can be cleared without opening the drawer. Editing lead content lives in Claude.
const LeadItem: React.FC<{
  lead: OutreachLead;
  today: string;
  model: StageModel;
  onSelect?: (lead: OutreachLead) => void;
  onDone?: (lead: OutreachLead) => void;
  onSnooze?: (lead: OutreachLead, dateStr: string, note: string) => void;
  onAdvance?: (lead: OutreachLead) => void;
}> = ({ lead, today, model, onSelect, onDone, onSnooze, onAdvance }) => {
  const due = dueOf(lead);
  const tone: "overdue" | "today" | "future" | "none" = !due
    ? "none"
    : due < today
    ? "overdue"
    : due === today
    ? "today"
    : "future";
  const dueLabel = !due
    ? "No date"
    : due < today
    ? `${moment(due).fromNow()}`
    : due === today
    ? "Today"
    : moment(due).format("MMM D");

  const canAdvance = model.nextStage(lead.stage) !== null;
  const hasActions = !!(onDone || onSnooze || (onAdvance && canAdvance));

  // Buttons live inside the row's onClick; stop propagation so acting on a lead
  // doesn't also open the drawer.
  const act = (fn?: (lead: OutreachLead) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn?.(lead);
  };

  return (
    <LeadRow role="button" onClick={() => onSelect?.(lead)}>
      <LeadMain>
        <LeadTitleLine>
          <LeadName>{lead.name}</LeadName>
          <Tag variant="neutral" size="sm">
            {channelLabel(lead.channel)}
          </Tag>
          <Tag variant={model.variant(lead.stage) as any} size="sm">
            {model.label(lead.stage)}
          </Tag>
        </LeadTitleLine>
        <LeadMeta>
          {(() => {
            const link = contactLink(lead);
            const text = [lead.company, lead.niche]
              .filter(Boolean)
              .join(" · ");
            // Display the handle/email as the link text, stripped of protocol
            // noise so long profile URLs stay readable.
            const linkText = (lead.handle || lead.email || "Open profile")
              .replace(/^https?:\/\/(www\.)?/i, "")
              .replace(/\/$/, "");
            if (!link) return text || lead.handle || "—";
            return (
              <>
                {text}
                {text ? " · " : ""}
                <MetaLink
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={link.label}
                  onClick={(e) => e.stopPropagation()}
                >
                  {linkText} ↗
                </MetaLink>
              </>
            );
          })()}
        </LeadMeta>
      </LeadMain>
      <RightSide>
        {hasActions && (
          <RowActions className="lead-row-actions">
            {onSnooze && (
              <SnoozeMenu
                onPick={(dateStr, note) => onSnooze(lead, dateStr, note)}
              />
            )}
            {onAdvance && canAdvance && (
              <Button variant="ghost" size="sm" onClick={act(onAdvance)}>
                Advance
              </Button>
            )}
            {onDone && (
              <Button variant="secondary" size="sm" onClick={act(onDone)}>
                Done
              </Button>
            )}
          </RowActions>
        )}
        <LeadAction>
          <NextActionText>
            {lead.nextAction || "No next action set"}
          </NextActionText>
          <DueBadge $tone={tone}>{dueLabel}</DueBadge>
        </LeadAction>
      </RightSide>
    </LeadRow>
  );
};

// Page size for queue sections. A 250-lead backlog renders 15 rows, not 250;
// the rest stays behind "Show more".
const QUEUE_PAGE = 15;
const QUEUE_STEP = 25;

type LeadRowActions = {
  model: StageModel;
  onSelect: (lead: OutreachLead) => void;
  onDone: (lead: OutreachLead) => void;
  onSnooze: (lead: OutreachLead, dateStr: string, note: string) => void;
  onAdvance: (lead: OutreachLead) => void;
};

// Windowed queue section: renders the first page of leads and expands in steps.
// Remount (via key) when filters change to collapse back to one page.
const PagedLeads: React.FC<
  { leads: OutreachLead[]; today: string } & LeadRowActions
> = ({ leads, today, ...rowActions }) => {
  const [shown, setShown] = useState(QUEUE_PAGE);
  const visible = leads.slice(0, shown);
  const remaining = leads.length - visible.length;
  return (
    <>
      <LeadList>
        {visible.map((l) => (
          <LeadItem key={l.id} lead={l} today={today} {...rowActions} />
        ))}
      </LeadList>
      {remaining > 0 && (
        <ShowMoreBar
          type="button"
          onClick={() => setShown((s) => s + QUEUE_STEP)}
        >
          Show {Math.min(QUEUE_STEP, remaining)} more · {remaining} hidden
        </ShowMoreBar>
      )}
    </>
  );
};

const SNOOZE_OPTIONS = [
  { label: "3d", days: 3 },
  { label: "1w", days: 7 },
  { label: "2w", days: 14 },
];

// Lead profile drawer: loads the full lead (conversation + touches), shows the
// thread, and offers inline actions (stage / snooze / mark done / next date) so
// micro-updates don't need Claude. Editing the lead's content stays in Claude.
const LeadDetail: React.FC<{
  lead: OutreachLead;
  today: string;
  accountId: string;
  model: StageModel;
  onClose: () => void;
  onUpdated: (lead: OutreachLead) => void;
}> = ({ lead, today, accountId, model, onClose, onUpdated }) => {
  const [full, setFull] = useState<OutreachLead | null>(null);
  const [loadingFull, setLoadingFull] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    setLoadingFull(true);
    getOutreachLead(lead.id, accountId)
      .then((l) => {
        if (alive && l) setFull(l);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoadingFull(false);
      });
    return () => {
      alive = false;
    };
  }, [lead.id, accountId]);

  const view = full ?? lead;
  const link = contactLink(view);
  const due = dueOf(view);
  const touches = [...(full?.touches || [])].sort((a, b) =>
    (b.at || "").localeCompare(a.at || "")
  );
  const conversation = full?.conversation || [];
  // Drafted copy the VA sends by hand. Pull the message fields out of the raw
  // `data` bag and render them as copy-ready cards; everything else in `data`
  // still shows under "Compiled data".
  const data = view.data || {};
  const msgChannel = (view.channel || "").toLowerCase();
  const MSG_FIELDS: Record<string, [string, string][]> = {
    ig: [
      ["First DM", "dm"],
      ["Follow-up 1 · case study", "ig_followup_1"],
      ["Follow-up 2 · video", "ig_followup_2"],
    ],
    li: [
      ["Connection note", "li_note"],
      ["Follow-up 1 · case study", "li_followup_1"],
      ["Follow-up 2 · video", "li_followup_2"],
    ],
  };
  const messages = (MSG_FIELDS[msgChannel] || [])
    .map(([label, key]) => [label, key, data[key]] as [string, string, any])
    .filter(([, , v]) => typeof v === "string" && v.trim().length > 0);
  const messageKeys = new Set(messages.map(([, key]) => key));
  const dataEntries = Object.entries(data).filter(
    ([k]) => !messageKeys.has(k)
  );

  // Cross-channel status: surface a matched LinkedIn (or below-ICP match) as a
  // header chip + link instead of burying it in the Compiled data dump.
  const matchStatus =
    typeof data.matchStatus === "string" ? data.matchStatus : undefined;
  const linkedinUrl =
    typeof data.linkedinUrl === "string" && /^https?:\/\//i.test(data.linkedinUrl)
      ? data.linkedinUrl
      : undefined;
  // Demo built for this prospect (Job D of the LinkedIn pipeline).
  const demoUrl =
    typeof data.demo_url === "string" && data.demo_url.trim().length > 0
      ? /^https?:\/\//i.test(data.demo_url)
        ? data.demo_url
        : `https://${data.demo_url}`
      : undefined;
  const MATCH_META: Record<string, { label: string; variant: string }> = {
    matched: { label: "Matched on LinkedIn", variant: "success" },
    "matched-below-icp": { label: "LinkedIn match · below ICP", variant: "neutral" },
    "ig-only": { label: "IG only", variant: "neutral" },
  };
  const matchMeta = matchStatus
    ? MATCH_META[matchStatus] ?? { label: matchStatus, variant: "neutral" }
    : undefined;

  const applyPatch = async (patch: Partial<OutreachLead>, note?: string) => {
    setSaving(true);
    setActionError(null);
    try {
      const updated = await updateOutreachLead({
        leadId: lead.id,
        patch,
        note,
        accountId,
      });
      setFull(updated);
      onUpdated(updated);
    } catch (e: any) {
      setActionError(e?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const nextStage = model.nextStage(view.stage);

  const snooze = (days: number) =>
    applyPatch(
      { nextActionAt: moment().add(days, "days").format("YYYY-MM-DD") },
      `Snoozed ${days}d`
    );
  const markDone = () =>
    applyPatch(
      { nextActionAt: "", lastContactedAt: new Date().toISOString() },
      "Marked done"
    );

  const fields: [string, string | undefined][] = [
    ["Company", view.company],
    ["Niche", view.niche],
    ["Handle", view.handle],
    ["Email", view.email],
    ["Owner", view.owner],
    ["Source", view.source],
    [
      "Last contacted",
      view.lastContactedAt
        ? moment(view.lastContactedAt).format("MMM D, YYYY")
        : undefined,
    ],
    [
      "Added",
      view.createdAt ? moment(view.createdAt).format("MMM D, YYYY") : undefined,
    ],
  ];
  const shownFields = fields.filter(([, v]) => !!v);
  const dueText = !due
    ? ""
    : due < today
    ? `overdue (${moment(due).format("MMM D")})`
    : due === today
    ? "due today"
    : `due ${moment(due).format("MMM D")}`;

  return (
    <Backdrop onClick={onClose}>
      <Drawer onClick={(e) => e.stopPropagation()}>
        <DrawerTop>
          <div>
            <DrawerTitle>{view.name}</DrawerTitle>
            <ChipRow>
              <Tag variant="neutral" size="sm">
                {channelLabel(view.channel)}
              </Tag>
              <Tag variant={model.variant(view.stage) as any} size="sm">
                {model.label(view.stage)}
              </Tag>
              {matchMeta && (
                <Tag variant={matchMeta.variant as any} size="sm">
                  {matchMeta.label}
                </Tag>
              )}
              {linkedinUrl && (
                <HdrLink
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn ↗
                </HdrLink>
              )}
              {demoUrl && (
                <HdrLink
                  href={demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Demo ↗
                </HdrLink>
              )}
            </ChipRow>
          </div>
          <CloseBtn aria-label="Close" onClick={onClose}>
            ×
          </CloseBtn>
        </DrawerTop>

        {link && (
          <ButtonWrap>
            <Button
              variant="primary"
              size="md"
              fullWidth
              onClick={() => window.open(link.href, "_blank", "noopener")}
            >
              {link.label}
            </Button>
          </ButtonWrap>
        )}

        <DetailSection>
          <DetailLabel>Quick actions</DetailLabel>
          <ActionsRow>
            <SelectShell>
              <span>Stage</span>
              <NativeSelect
                value={view.stage}
                disabled={saving}
                onChange={(e) =>
                  applyPatch({ stage: e.target.value as LeadStage })
                }
              >
                {/* A lead can sit on a stage that was since removed from the
                    config — keep it selectable so the drawer isn't blank. */}
                {!model.isKnown(view.stage) && (
                  <option value={view.stage}>
                    {model.label(view.stage)} (archived)
                  </option>
                )}
                {model.stages.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </NativeSelect>
              <IconChevronDown size={12} />
            </SelectShell>
            {nextStage && (
              <Button
                variant="secondary"
                size="sm"
                disabled={saving}
                onClick={() =>
                  applyPatch(
                    { stage: nextStage },
                    `Advanced to ${model.label(nextStage)}`
                  )
                }
              >
                Advance to {model.label(nextStage)}
              </Button>
            )}
          </ActionsRow>
          <ActionsRow style={{ marginTop: "var(--space-2)" }}>
            {SNOOZE_OPTIONS.map((o) => (
              <Button
                key={o.days}
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={() => snooze(o.days)}
              >
                Snooze {o.label}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={markDone}
            >
              Mark done
            </Button>
            <DateInput
              type="date"
              title="Set next action date"
              value={due || ""}
              disabled={saving}
              onChange={(e) =>
                e.target.value && applyPatch({ nextActionAt: e.target.value })
              }
            />
          </ActionsRow>
          {saving && <ActionStatus>Saving…</ActionStatus>}
          {actionError && <ActionError>{actionError}</ActionError>}
        </DetailSection>

        {(view.nextAction || due) && (
          <DetailSection>
            <DetailLabel>Next action</DetailLabel>
            <Para>
              {view.nextAction || "No action set"}
              {dueText ? `  ·  ${dueText}` : ""}
            </Para>
          </DetailSection>
        )}

        {shownFields.length > 0 && (
          <DetailSection>
            <DetailLabel>Details</DetailLabel>
            <FieldGrid>
              {shownFields.map(([k, v]) => (
                <React.Fragment key={k}>
                  <FieldKey>{k}</FieldKey>
                  <FieldVal>{v}</FieldVal>
                </React.Fragment>
              ))}
            </FieldGrid>
          </DetailSection>
        )}

        {view.notes && (
          <DetailSection>
            <DetailLabel>Notes</DetailLabel>
            <Para>{view.notes}</Para>
          </DetailSection>
        )}

        {messages.length > 0 && (
          <DetailSection>
            <DetailLabel>Messages to send</DetailLabel>
            {messages.map(([label, key, text]) => (
              <MsgCard key={key}>
                <MsgCardHead>
                  <MsgCardLabel>{label}</MsgCardLabel>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyText(text as string)}
                  >
                    Copy
                  </Button>
                </MsgCardHead>
                <Para>{text as string}</Para>
              </MsgCard>
            ))}
          </DetailSection>
        )}

        <DetailSection>
          <DetailLabel>Conversation</DetailLabel>
          {loadingFull ? (
            <Skeleton width="100%" height={80} />
          ) : conversation.length === 0 ? (
            <Para>
              No messages yet. Paste the thread through Claude to see it here and
              spot where it's getting stuck.
            </Para>
          ) : (
            <Thread>
              {conversation.map((m, i) => {
                const me = m.from === "me";
                return (
                  <div key={i}>
                    <MsgRow $me={me}>
                      <Bubble $me={me}>{m.text}</Bubble>
                    </MsgRow>
                    {m.at && (
                      <MsgMeta $me={me}>
                        {moment(m.at).format("MMM D, h:mma")}
                      </MsgMeta>
                    )}
                  </div>
                );
              })}
            </Thread>
          )}
        </DetailSection>

        {dataEntries.length > 0 && (
          <DetailSection>
            <DetailLabel>Compiled data</DetailLabel>
            <FieldGrid>
              {dataEntries.map(([k, v]) => (
                <React.Fragment key={k}>
                  <FieldKey>{k}</FieldKey>
                  <FieldVal>
                    {typeof v === "object" ? JSON.stringify(v) : String(v)}
                  </FieldVal>
                </React.Fragment>
              ))}
            </FieldGrid>
          </DetailSection>
        )}

        {!loadingFull && touches.length > 0 && (
          <DetailSection>
            <DetailLabel>Activity</DetailLabel>
            {touches.map((t, i) => (
              <TouchItem key={i}>
                <TouchTime>
                  {t.at ? moment(t.at).format("MMM D, YYYY · h:mma") : ""}
                </TouchTime>
                <Para>{t.note}</Para>
              </TouchItem>
            ))}
          </DetailSection>
        )}
      </Drawer>
    </Backdrop>
  );
};

// ─── Stage editor (Manage stages) ─────────────────────────────────────────────

const ModalCard = styled.div`
  width: min(680px, 100%);
  max-height: 88vh;
  margin: auto;
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
`;

const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-5) var(--space-5) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const ModalTitle = styled.h2`
  font-family: var(--font-display);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0 0 4px;
`;

const ModalSub = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin: 0;
`;

const ModalBody = styled.div`
  padding: var(--space-4) var(--space-5);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const ModalFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-5) var(--space-5);
  border-top: 1px solid var(--color-border-subtle);
`;

const StageRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 130px auto;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;

const ReorderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const IconBtn = styled.button`
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  width: 22px;
  height: 18px;
  line-height: 1;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  &:hover:not(:disabled) {
    color: var(--color-text);
    border-color: var(--color-accent);
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const StageFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const KeyLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const KeyInput = styled.input`
  height: 26px;
  width: 150px;
  padding: 0 var(--space-2);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: var(--font-mono, monospace);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const ToneSelect = styled.select`
  height: 32px;
  padding: 0 var(--space-2);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const TerminalWrap = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

const Swatch = styled.span<{ $c: string }>`
  width: 10px;
  height: 10px;
  border-radius: var(--radius-full);
  background: ${(p) => p.$c};
  flex-shrink: 0;
`;

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const StageEditor: React.FC<{
  initial: StageDef[];
  leadCounts: Record<string, number>;
  onClose: () => void;
  onSave: (stages: StageDef[]) => Promise<void>;
}> = ({ initial, leadCounts, onClose, onSave }) => {
  const [rows, setRows] = useState<StageDef[]>(() =>
    initial.map((s) => ({ ...s }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const patch = (i: number, next: Partial<StageDef>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...next } : r)));

  const move = (i: number, dir: -1 | 1) =>
    setRows((rs) => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const copy = [...rs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const remove = (i: number) => {
    const row = rows[i];
    const count = leadCounts[row.key] || 0;
    if (
      count > 0 &&
      !window.confirm(
        `${count} lead${count === 1 ? "" : "s"} on "${
          row.label || row.key
        }". Deleting the stage keeps those leads but moves them to an archived column until you re-stage them. Delete anyway?`
      )
    )
      return;
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  };

  const add = () =>
    setRows((rs) => [...rs, { key: "", label: "", tone: "neutral" }]);

  const save = async () => {
    setError(null);
    // Normalize: derive a slug key from the label when the key is blank.
    const normalized: StageDef[] = rows.map((r) => {
      const label = r.label.trim();
      const key = slugify(r.key || label);
      const out: StageDef = { key, label, tone: r.tone || "neutral" };
      if (r.terminal) {
        out.terminal = true;
        out.outcome = r.outcome === "negative" ? "negative" : "positive";
      }
      return out;
    });

    for (const r of normalized) {
      if (!r.label) return setError("Every stage needs a label.");
      if (!/^[a-z0-9_]+$/.test(r.key))
        return setError(`"${r.label}" has an invalid key.`);
    }
    const keys = normalized.map((r) => r.key);
    const dupe = keys.find((k, i) => keys.indexOf(k) !== i);
    if (dupe) return setError(`Duplicate stage key "${dupe}".`);
    if (!normalized.some((r) => !r.terminal))
      return setError("Keep at least one non-terminal (funnel) stage.");

    setSaving(true);
    try {
      await onSave(normalized);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Could not save stages.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Backdrop
      onClick={() => !saving && onClose()}
      style={{ alignItems: "center", padding: "var(--space-4)" }}
    >
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <div>
            <ModalTitle>Manage pipeline stages</ModalTitle>
            <ModalSub>
              Order is the funnel order. Terminal stages (won/lost) sit at the end
              and are hidden from the active board.
            </ModalSub>
          </div>
          <CloseBtn aria-label="Close" onClick={() => !saving && onClose()}>
            ×
          </CloseBtn>
        </ModalHead>

        <ModalBody>
          {rows.map((r, i) => {
            const count = leadCounts[r.key] || 0;
            return (
              <StageRow key={i}>
                <ReorderCol>
                  <IconBtn
                    type="button"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    aria-label="Move up"
                  >
                    ▲
                  </IconBtn>
                  <IconBtn
                    type="button"
                    disabled={i === rows.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label="Move down"
                  >
                    ▼
                  </IconBtn>
                </ReorderCol>

                <StageFields>
                  <Input
                    value={r.label}
                    placeholder="Stage name"
                    onChange={(e) => patch(i, { label: e.target.value })}
                  />
                  <KeyLine>
                    <Swatch $c={TONE_DOT[r.tone || "neutral"]} />
                    <KeyInput
                      value={r.key}
                      placeholder={slugify(r.label) || "key"}
                      onChange={(e) =>
                        patch(i, { key: slugify(e.target.value) })
                      }
                      title="Stage key used by automations (lowercase, no spaces)"
                    />
                    {count > 0 && (
                      <Tag variant="neutral" size="sm">
                        {count} lead{count === 1 ? "" : "s"}
                      </Tag>
                    )}
                  </KeyLine>
                </StageFields>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <ToneSelect
                    value={r.tone || "neutral"}
                    onChange={(e) =>
                      patch(i, { tone: e.target.value as StageTone })
                    }
                  >
                    {STAGE_TONES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </ToneSelect>
                  <TerminalWrap>
                    <input
                      type="checkbox"
                      checked={!!r.terminal}
                      onChange={(e) =>
                        patch(i, {
                          terminal: e.target.checked,
                          outcome: e.target.checked
                            ? r.outcome || "positive"
                            : undefined,
                        })
                      }
                    />
                    Terminal
                  </TerminalWrap>
                  {r.terminal && (
                    <ToneSelect
                      value={r.outcome || "positive"}
                      onChange={(e) =>
                        patch(i, {
                          outcome: e.target.value as "positive" | "negative",
                        })
                      }
                    >
                      <option value="positive">Won</option>
                      <option value="negative">Lost</option>
                    </ToneSelect>
                  )}
                </div>

                <IconBtn
                  type="button"
                  onClick={() => remove(i)}
                  aria-label="Delete stage"
                  style={{ width: 28, height: 28, fontSize: 14 }}
                >
                  ×
                </IconBtn>
              </StageRow>
            );
          })}

          <div>
            <Button variant="ghost" size="sm" onClick={add}>
              + Add stage
            </Button>
          </div>
          {error && <ActionError>{error}</ActionError>}
        </ModalBody>

        <ModalFoot>
          <ModalSub>{rows.length} stages</ModalSub>
          <ActionsRow>
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={saving}
              onClick={save}
            >
              Save stages
            </Button>
          </ActionsRow>
        </ModalFoot>
      </ModalCard>
    </Backdrop>
  );
};

// ─── Worker activity modal (Ops home) ─────────────────────────────────────────
// Opening a worker lane shows what that worker did over a period (today / this
// week / this month). Every figure is derived from the leads + drafts already
// loaded, so it works with no extra backend. Sourcing is exact (lead.createdAt);
// the write/send/reply views read the current queue plus period activity from
// updatedAt + stage, which fills in as the daily run logs touches.

type WorkerKey = "source" | "write" | "send" | "reply";
type Period = "today" | "week" | "month";

type OpsSummary = {
  unworked: number;
  draftsToReview: number;
  draftErrors: number;
  approved: number;
  inFlight: number;
  humanDue: number;
  [k: string]: any;
};

const PERIODS: { key: Period; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
];

// Each lane of the daily run, named for what it does. Deliberately plain: this
// screen is white label, so no invented personas and no personal names.
const WORKER_META: Record<
  WorkerKey,
  { name: string; role: string; step: string; subtitle: string }
> = {
  source: {
    name: "Source",
    role: "Sourcing",
    step: "1 · Source",
    subtitle: "New leads discovered and added to the tracker.",
  },
  write: {
    name: "Write",
    role: "Writing",
    step: "2 · Write",
    subtitle: "Personalized drafts written and queued for review.",
  },
  send: {
    name: "Send",
    role: "Sending",
    step: "3 · Send",
    subtitle: "Messages shipped and handed to a sequence.",
  },
  reply: {
    name: "Reply",
    role: "Replies",
    step: "4 · Reply",
    subtitle: "Prospects who replied or engaged, handed back to you.",
  },
};

const KIND_LABEL: Record<string, string> = {
  li_dm: "LinkedIn",
  ig_dm: "Instagram",
  x_dm: "X",
  email: "Email",
};

const WBig = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-4xl, var(--font-size-3xl));
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  color: var(--color-text);
`;

const WBigLabel = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-left: var(--space-2);
`;

const WBigSub = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: var(--space-2);
`;

const WNow = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  padding: var(--space-3);
  margin-top: var(--space-4);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
`;

const BreakWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const BreakChip = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const BreakN = styled.span`
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const RecentWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const RecentRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-1);
  border-bottom: 1px solid var(--color-border-subtle);
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${(p) =>
      p.$clickable ? "var(--color-surface-subtle)" : "transparent"};
  }
`;

const RecentName = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
`;

const RecentMeta = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const RecentTime = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  white-space: nowrap;
  flex-shrink: 0;
`;

const countBy = <T,>(
  items: T[],
  keyFn: (i: T) => string | undefined
): { label: string; n: number }[] => {
  const m = new Map<string, number>();
  items.forEach((i) => {
    const k = keyFn(i);
    if (k) m.set(k, (m.get(k) || 0) + 1);
  });
  return Array.from(m.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, n]) => ({ label, n }));
};

const WorkerModal: React.FC<{
  worker: WorkerKey;
  leads: OutreachLead[];
  drafts: OutreachDraft[];
  model: StageModel;
  today: string;
  ops: OpsSummary;
  onClose: () => void;
  onNavigate: (tabId: number) => void;
  onSelectLead: (lead: OutreachLead) => void;
}> = ({ worker, leads, drafts, model, ops, onClose, onNavigate, onSelectLead }) => {
  const [period, setPeriod] = useState<Period>("today");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const meta = WORKER_META[worker];

  const view = useMemo(() => {
    const from =
      period === "today"
        ? moment().startOf("day")
        : period === "week"
        ? moment().startOf("week")
        : moment().startOf("month");
    const inPeriod = (ts?: string) =>
      !!ts && moment(ts).isSameOrAfter(from);

    type RecentItem = {
      key: string;
      name: string;
      meta: string;
      at?: string;
      lead?: OutreachLead;
    };
    const recentFromLeads = (ls: OutreachLead[], at: (l: OutreachLead) => string | undefined) =>
      [...ls]
        .sort((a, b) => (at(b) || "").localeCompare(at(a) || ""))
        .slice(0, 12)
        .map<RecentItem>((l) => ({
          key: l.id,
          name: l.name,
          meta:
            [channelLabel(l.channel), model.label(l.stage), l.niche]
              .filter(Boolean)
              .join(" · ") || "—",
          at: at(l),
          lead: l,
        }));

    if (worker === "source") {
      const pool = leads.filter((l) => inPeriod(l.createdAt));
      return {
        value: pool.length,
        valueLabel: "leads sourced",
        valueSub: "New records added to the tracker in this period.",
        now: `${ops.unworked} waiting to be worked right now`,
        breakdown: countBy(pool, (l) => channelLabel(l.channel)),
        recent: recentFromLeads(pool, (l) => l.createdAt),
        goTo: 1,
        goToLabel: "Open Leads",
      };
    }
    if (worker === "write") {
      const pool = drafts.filter((d) => inPeriod(d.createdAt));
      return {
        value: pool.length,
        valueLabel: "drafts written",
        valueSub: "Personalized drafts queued for review in this period.",
        now:
          `${ops.draftsToReview} waiting to review right now` +
          (ops.draftErrors > 0 ? ` · ${ops.draftErrors} errored` : ""),
        breakdown: countBy(pool, (d) => KIND_LABEL[d.kind] || d.kind),
        recent: [...pool]
          .sort((a, b) =>
            (b.createdAt || "").localeCompare(a.createdAt || "")
          )
          .slice(0, 12)
          .map((d) => ({
            key: d.id,
            name: d.leadName,
            meta: [KIND_LABEL[d.kind] || d.kind, d.status]
              .filter(Boolean)
              .join(" · "),
            at: d.createdAt,
            lead: undefined as OutreachLead | undefined,
          })),
        goTo: 3,
        goToLabel: "Open Review",
      };
    }
    if (worker === "send") {
      const pool = leads.filter(
        (l) => isAutomatedStep(l) && inPeriod(l.updatedAt)
      );
      return {
        value: pool.length,
        valueLabel: "moved into automation",
        valueSub:
          "Leads a sequence started or advanced in this period (invite sent, follow-up sent, cold email queued).",
        now: `${ops.approved} ready to send · ${ops.inFlight} in flight right now`,
        breakdown: countBy(pool, (l) => channelLabel(l.channel)),
        recent: recentFromLeads(pool, (l) => l.updatedAt),
        goTo: 3,
        goToLabel: "Open Review",
      };
    }
    // reply
    const repliedKey =
      model.funnelKeys.find((k) => k === "replied") || model.funnelKeys[2];
    const repliedIdx = repliedKey ? model.funnelIndex(repliedKey) : 2;
    const engaged = (l: OutreachLead) =>
      model.isActive(l.stage) && model.funnelIndex(l.stage) >= repliedIdx;
    const pool = leads.filter((l) => engaged(l) && inPeriod(l.updatedAt));
    return {
      value: pool.length,
      valueLabel: "leads engaged",
      valueSub:
        "Replied / interested / meeting leads with activity in this period.",
      now: `${ops.humanDue} to handle right now`,
      breakdown: countBy(pool, (l) => model.label(l.stage)),
      recent: recentFromLeads(pool, (l) => l.updatedAt),
      goTo: 0,
      goToLabel: "Open Today",
    };
  }, [worker, period, leads, drafts, model, ops]);

  const periodLabel =
    PERIODS.find((p) => p.key === period)?.label ?? "Today";

  return (
    <Backdrop
      onClick={onClose}
      style={{ alignItems: "center", padding: "var(--space-4)" }}
    >
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <div>
            <ModalTitle>{meta.name}</ModalTitle>
            <ModalSub>
              {meta.role} · {meta.step} · {meta.subtitle}
            </ModalSub>
          </div>
          <CloseBtn aria-label="Close" onClick={onClose}>
            ×
          </CloseBtn>
        </ModalHead>

        <ModalBody>
          <ChannelChips role="group" aria-label="Period">
            {PERIODS.map((p) => (
              <ChannelChip
                key={p.key}
                type="button"
                $active={period === p.key}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </ChannelChip>
            ))}
          </ChannelChips>

          <div style={{ marginTop: "var(--space-4)" }}>
            <WBig>
              {view.value.toLocaleString()}
              <WBigLabel>{view.valueLabel}</WBigLabel>
            </WBig>
            <WBigSub>{view.valueSub}</WBigSub>
          </div>

          <WNow>{view.now}</WNow>

          {view.breakdown.length > 0 && (
            <div style={{ marginTop: "var(--space-4)" }}>
              <DetailLabel>Breakdown</DetailLabel>
              <BreakWrap>
                {view.breakdown.map((b) => (
                  <BreakChip key={b.label}>
                    {b.label} <BreakN>{b.n}</BreakN>
                  </BreakChip>
                ))}
              </BreakWrap>
            </div>
          )}

          <div style={{ marginTop: "var(--space-4)" }}>
            <DetailLabel>Recent</DetailLabel>
            {view.recent.length === 0 ? (
              <Para>Nothing in this period.</Para>
            ) : (
              <RecentWrap>
                {view.recent.map((r) => (
                  <RecentRow
                    key={r.key}
                    $clickable={!!r.lead}
                    onClick={() => r.lead && onSelectLead(r.lead)}
                  >
                    <div style={{ minWidth: 0 }}>
                      <RecentName>{r.name}</RecentName>{" "}
                      <RecentMeta>{r.meta}</RecentMeta>
                    </div>
                    <RecentTime>
                      {r.at ? moment(r.at).fromNow() : ""}
                    </RecentTime>
                  </RecentRow>
                ))}
              </RecentWrap>
            )}
          </div>
        </ModalBody>

        <ModalFoot>
          <ModalSub>{periodLabel}</ModalSub>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onNavigate(view.goTo)}
          >
            {view.goToLabel} →
          </Button>
        </ModalFoot>
      </ModalCard>
    </Backdrop>
  );
};

// Boards that no longer hold leads. "tribed" ran in parallel with
// "digital_university" until 2026-08-08, when its 220 leads were merged in.
const RETIRED_OUTREACH_ACCOUNTS = new Set(["tribed"]);

const OutreachDashboard: React.FC<{ communityId?: string }> = ({
  communityId,
}) => {
  // Outreach leads live under Outreach/{accountId}/leads, where accountId is the
  // community id. There is no default account: the screen operates on an
  // explicitly selected community. The signed-in admin's community is only the
  // initial selection when it actually has an outreach board.
  const [accounts, setAccounts] = useState<OutreachAccountSummary[] | null>(
    null
  );
  const [accountId, setAccountId] = useState("");
  const [leads, setLeads] = useState<OutreachLead[] | null>(null);
  const [drafts, setDrafts] = useState<OutreachDraft[]>([]);
  const [lastRun, setLastRun] = useState<OutreachRunSummary | null>(null);
  const [stageConfig, setStageConfig] = useState<StageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(TAB_TODAY);
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<OutreachLead | null>(null);
  const [quickError, setQuickError] = useState<string | null>(null);
  const [editingStages, setEditingStages] = useState(false);
  // The demoted surfaces. Each opens from the number it explains, so none of
  // them is a tab competing with the work queue for the reader's attention.
  const [showDrafts, setShowDrafts] = useState(false);
  const [showRails, setShowRails] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Which lane's throughput modal is open, if any.
  const [openWorker, setOpenWorker] = useState<WorkerKey | null>(null);

  // The pipeline model is derived from the account's stage config (or the
  // built-in defaults until it loads / when none is saved). Everything
  // stage-related on the screen reads through it.
  const model = useMemo(() => buildStageModel(stageConfig), [stageConfig]);

  // Which communities have an outreach board. Loads once; picks the initial
  // account: the admin's own community if it has a board, else the only board
  // when there is exactly one, else nothing (the user picks).
  useEffect(() => {
    let cancelled = false;
    listOutreachAccounts()
      .then((raw) => {
        if (cancelled) return;
        // Retired boards still have documents in Firestore, so they keep showing
        // up here. Hide them: picking one opens an empty pipeline beside the real
        // one, and anything added lands where nothing reads it. Mirrors
        // RETIRED_OUTREACH_ACCOUNTS in mcp/src/tools/outreach.ts.
        const list = raw.filter((a) => !RETIRED_OUTREACH_ACCOUNTS.has(a.id));
        setAccounts(list);
        setAccountId((prev) => {
          if (prev && list.some((a) => a.id === prev)) return prev;
          const own = (communityId || "").trim();
          if (list.some((a) => a.id === own)) return own;
          return list.length === 1 ? list[0].id : "";
        });
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [communityId]);

  const load = useCallback(async () => {
    // No community selected yet: show the picker prompt rather than falling
    // back to another account's leads.
    if (!accountId) {
      setLeads([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSelected(null);
    // Drop the previous account's data BEFORE the fetch. The screen no longer
    // blocks on this call, so anything left here stays on screen and stays
    // clickable, and every write carries the NEW accountId. Patching account
    // A's lead id under account B is the bug this prevents; the null lead list
    // is what the skeletons key off.
    setLeads(null);
    setDrafts([]);
    setLastRun(null);
    try {
      const [leadRes, stageRes, draftRes] = await Promise.all([
        getOutreachLeads(accountId),
        getOutreachStages(accountId).catch(() => null),
        // The review queue is additive: a load failure (e.g. callable not
        // deployed yet) must not take down the whole board.
        listOutreachDrafts(accountId).catch(() => ({
          drafts: [] as OutreachDraft[],
          lastRun: null,
        })),
      ]);
      setLeads(leadRes);
      setDrafts(draftRes.drafts);
      setLastRun(draftRes.lastRun);
      // Replace (not merge) so switching accounts never shows the previous
      // account's pipeline.
      setStageConfig(stageRes ? { stages: stageRes.stages } : null);
    } catch (e: any) {
      setError(e?.message || "Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  // Merge a review result into the drafts list so the queue reflects it
  // without a full reload.
  const applyDraftUpdate = useCallback((updated: OutreachDraft) => {
    setDrafts((prev) =>
      prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d))
    );
  }, []);

  // Merge an inline-action result into the list + the open drawer so the board
  // reflects it without a full reload.
  const applyLeadUpdate = useCallback((updated: OutreachLead) => {
    setLeads((prev) =>
      prev
        ? prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l))
        : prev
    );
    setSelected((prev) =>
      prev && prev.id === updated.id ? { ...prev, ...updated } : prev
    );
  }, []);

  // Optimistic write used by the board drag and the inline Today actions: apply
  // the patch locally so the row moves instantly, then reconcile with the server
  // result — or roll back to the snapshot if the write fails.
  const patchLead = useCallback(
    async (
      lead: OutreachLead,
      patch: Partial<OutreachLead>,
      note: string
    ) => {
      const snapshot = { ...lead };
      setQuickError(null);
      applyLeadUpdate({ ...lead, ...patch });
      try {
        const updated = await updateOutreachLead({
          leadId: lead.id,
          patch,
          note,
          accountId,
        });
        applyLeadUpdate(updated);
      } catch (e: any) {
        applyLeadUpdate(snapshot);
        setQuickError(
          e?.message || `Could not update ${lead.name}. Please try again.`
        );
      }
    },
    [applyLeadUpdate, accountId]
  );

  const moveLeadStage = useCallback(
    (lead: OutreachLead, stage: LeadStage) => {
      if (lead.stage === stage) return;
      patchLead(lead, { stage }, `Moved to ${model.label(stage)}`);
    },
    [patchLead, model]
  );

  // Reschedule a follow-up to a specific date (used by the inline snooze menu and
  // the bulk "push all" action).
  const snoozeLead = useCallback(
    (lead: OutreachLead, dateStr: string, label: string) =>
      patchLead(lead, { nextActionAt: dateStr }, label),
    [patchLead]
  );

  const pushAll = useCallback(
    (list: OutreachLead[], dateStr: string, label: string) =>
      list.forEach((l) => patchLead(l, { nextActionAt: dateStr }, label)),
    [patchLead]
  );

  const completeLead = useCallback(
    (lead: OutreachLead) =>
      patchLead(
        lead,
        { nextActionAt: "", lastContactedAt: new Date().toISOString() },
        "Marked done"
      ),
    [patchLead]
  );

  const advanceLead = useCallback(
    (lead: OutreachLead) => {
      const next = model.nextStage(lead.stage);
      if (next)
        patchLead(lead, { stage: next }, `Advanced to ${model.label(next)}`);
    },
    [patchLead, model]
  );

  const saveStages = useCallback(
    async (stages: StageDef[]) => {
      const res = await setOutreachStages({ stages, accountId });
      setStageConfig({ stages: res.stages });
    },
    [accountId]
  );

  const all = useMemo(() => leads ?? [], [leads]);
  const today = moment().format("YYYY-MM-DD");

  // ONE pass, ONE definition per concept. The screen used to derive "needs a
  // human", "no next action" and "in automation" in three places with three
  // populations, so the tab badge, the ops lane and the queue heading could all
  // print a different answer to the same question. See components/queueModel.ts.
  //
  // A count is never filtered. Only a visible list is. Filtering a count is
  // what made "total active" change when a channel was picked.
  const queue = useMemo(
    () => buildQueueModel(all, drafts, model, today),
    [all, drafts, model, today]
  );

  const ops = useMemo(() => opsSummaryOf(queue, lastRun), [queue, lastRun]);

  // The judgement half: the verdict, the findings and the rails. It does not
  // depend on the leads, so the default view paints while 1,500 of them are
  // still in flight.
  const analysis = useOutreachAnalysis(accountId);

  // The channel filter narrows the lists a person reads. It never reaches a
  // count.
  const visibleQueueLeads = useMemo(() => {
    const scoped = (list: OutreachLead[]) =>
      channelFilter === "all"
        ? list
        : list.filter((l) => l.channel === channelFilter);
    return { overdue: scoped(queue.overdue), dueToday: scoped(queue.dueToday) };
  }, [queue, channelFilter]);

  const filteredLeads = useMemo(() => {
    let out = all;
    if (channelFilter !== "all")
      out = out.filter((l) => l.channel === channelFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((l) =>
        `${l.name} ${l.company || ""} ${l.niche || ""} ${l.handle || ""} ${
          l.email || ""
        }`
          .toLowerCase()
          .includes(q)
      );
    }
    return [...out].sort((a, b) => dueSortKey(a).localeCompare(dueSortKey(b)));
  }, [all, channelFilter, search]);

  const lastUpdated = queue.lastUpdated
    ? `Updated ${moment(queue.lastUpdated).fromNow()}`
    : undefined;

  const accountPicker =
    accounts && accounts.length > 0 ? (
      <SelectShell>
        <span>Community</span>
        <NativeSelect
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          aria-label="Outreach community"
        >
          {!accountId && <option value="">Select…</option>}
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.id} ({a.leadCount})
            </option>
          ))}
        </NativeSelect>
        <IconChevronDown size={14} />
      </SelectShell>
    ) : null;

  const toolbar = (
    <Toolbar>
      {accountPicker}
      {lastUpdated && <UpdatedText>{lastUpdated}</UpdatedText>}
      {accountId && (
        <Button
          variant="ghost"
          size="sm"
          disabled={!leads}
          title={
            leads
              ? undefined
              : "Waiting for the leads, so a stage delete can warn you about the ones on it."
          }
          onClick={() => setEditingStages(true)}
        >
          Manage stages
        </Button>
      )}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          // Both halves. The verdict and the findings come from their own
          // fetch, so a Refresh that only reloaded leads would leave yesterday's
          // analysis on screen after the routine had already posted today's.
          load();
          analysis.reload();
        }}
        isLoading={loading}
      >
        Refresh
      </Button>
    </Toolbar>
  );

  const stageEditor = editingStages ? (
    <StageEditor
      initial={model.stages}
      leadCounts={queue.leadCountByStage}
      onClose={() => setEditingStages(false)}
      onSave={saveStages}
    />
  ) : null;

  // One pill per tab. Today's is the ONE work count, computed where the queue
  // heading computes it, never a second copy of the expression.
  const workTotal = workQueueTotal(analysis, queue, !!leads);
  const tabBadges: Record<
    number,
    { count: number; tone: "danger" | "accent" | "neutral" } | null
  > = {
    [TAB_TODAY]:
      workTotal > 0
        ? {
            count: workTotal,
            tone:
              analysis.openP0 > 0 || queue.overdue.length > 0
                ? "danger"
                : "accent",
          }
        : null,
    [TAB_LEADS]: all.length > 0 ? { count: all.length, tone: "neutral" } : null,
  };

  const LANES: {
    key: WorkerKey;
    value: number;
    valueLabel: string;
    tone: "danger" | "accent" | "muted";
    sub: string;
  }[] = [
    {
      key: "source",
      value: ops.unworked,
      valueLabel: "to work",
      tone: ops.unworked > 0 ? "accent" : "muted",
      sub: `${ops.newToday} added today · ${ops.discoveredToday} found last run`,
    },
    {
      key: "write",
      value: ops.draftsToReview,
      valueLabel: "to review",
      tone: ops.draftsToReview > 0 ? "accent" : "muted",
      sub:
        (ops.draftErrors > 0 ? `${ops.draftErrors} failed · ` : "") +
        `${ops.draftedToday} drafted last run`,
    },
    {
      key: "send",
      value: ops.approved,
      valueLabel: "ready to send",
      tone: ops.approved > 0 ? "accent" : "muted",
      sub: `${ops.inFlight} in flight · ${ops.shippedToday} sent last run`,
    },
    {
      key: "reply",
      value: ops.humanDue,
      valueLabel: "to handle",
      tone: ops.humanDue > 0 ? "danger" : "muted",
      sub:
        ops.humanDue > 0 ? "Replies and follow-ups you owe now" : "All caught up",
    },
  ];

  // The four lanes of the daily run, with the period breakdown behind each.
  // They no longer sit on the page: every number here is already a row in the
  // work queue. What they add is throughput over time, so they live inside the
  // rail detail surface.
  const throughput = (
    <>
      <OpsCaption>
        {ops.ranAt
          ? `Last run ${moment(ops.ranAt).fromNow()}.`
          : "No run recorded yet."}{" "}
        Source, write, send, reply. Open a lane for today, this week and this
        month.
      </OpsCaption>
      <LaneGrid>
        {LANES.map((lane) => (
          <Lane
            key={lane.key}
            type="button"
            onClick={() => setOpenWorker(lane.key)}
          >
            <LaneStep>
              <LaneDot $tone={lane.tone} />
              {WORKER_META[lane.key].step}
            </LaneStep>
            <LaneName>{WORKER_META[lane.key].name}</LaneName>
            <LaneBig $tone={lane.tone}>
              {lane.value}
              <LaneBigLabel>{lane.valueLabel}</LaneBigLabel>
            </LaneBig>
            <LaneSub>{lane.sub}</LaneSub>
          </Lane>
        ))}
      </LaneGrid>
    </>
  );

  // ── Today ─────────────────────────────────────────────────────────────────
  // The verdict, then the one work queue, then the rails, then the results.
  // Judgement first, throughput second, reference last.

  // The follow-up list the queue expands into. It lives here because the rows
  // and their drawer do.
  const rowActions: LeadRowActions = {
    model,
    onSelect: setSelected,
    onDone: completeLead,
    onSnooze: snoozeLead,
    onAdvance: advanceLead,
  };
  const confirmPush = (list: OutreachLead[]) => {
    if (
      list.length > 20 &&
      !window.confirm(
        `Reschedule all ${list.length} follow-ups to tomorrow? This updates every lead in the list, not just the visible rows.`
      )
    )
      return;
    pushAll(
      list,
      moment().add(1, "days").format("YYYY-MM-DD"),
      "Snoozed to tomorrow"
    );
  };

  const leadSection = (
    <>
      <FilterRow>
        <ChannelFilter
          value={channelFilter}
          onChange={setChannelFilter}
          channels={queue.channels}
        />
        {visibleQueueLeads.overdue.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => confirmPush(visibleQueueLeads.overdue)}
          >
            Push {visibleQueueLeads.overdue.length} overdue to tomorrow
          </Button>
        )}
        {visibleQueueLeads.dueToday.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => confirmPush(visibleQueueLeads.dueToday)}
          >
            Push {visibleQueueLeads.dueToday.length} due today to tomorrow
          </Button>
        )}
      </FilterRow>
      {visibleQueueLeads.dueToday.length > 0 && (
        <>
          <QueueGroupLabel>Due today</QueueGroupLabel>
          <PagedLeads
            key={`dueToday:${channelFilter}`}
            leads={visibleQueueLeads.dueToday}
            today={today}
            {...rowActions}
          />
        </>
      )}
      {visibleQueueLeads.overdue.length > 0 && (
        <>
          <QueueGroupLabel>Overdue</QueueGroupLabel>
          <PagedLeads
            key={`overdue:${channelFilter}`}
            leads={visibleQueueLeads.overdue}
            today={today}
            {...rowActions}
          />
        </>
      )}
      {visibleQueueLeads.overdue.length === 0 &&
        visibleQueueLeads.dueToday.length === 0 && (
          <QueueGroupLabel>
            Nothing on this channel. Clear the filter to see the rest.
          </QueueGroupLabel>
        )}
    </>
  );

  const renderToday = () => {
    const f = queue.funnel;
    const replyRate =
      f.contacted > 0
        ? `${((f.replied / f.contacted) * 100).toFixed(0)}%`
        : "-";
    const closed = f.won + f.lost;
    const winRate =
      closed > 0 ? `${((f.won / closed) * 100).toFixed(0)}%` : "-";
    const hops = f.steps.slice(1).map((step, i) => {
      const from = f.steps[i];
      return {
        label: `${from.label} to ${step.label}`,
        rate:
          from.count > 0
            ? `${((step.count / from.count) * 100).toFixed(0)}%`
            : "-",
      };
    });
    const bottleneck = computeBottleneck(f.steps);

    return (
      <SectionSpacing>
        <DailyAnalysis
          analysis={analysis}
          today={today}
          onOpenHistory={() => setShowHistory(true)}
        />

        <WorkQueue
          analysis={analysis}
          queue={queue}
          leadsReady={!!leads}
          onOpenDrafts={() => setShowDrafts(true)}
          leadSection={leadSection}
          closedFindings={analysis.closedFindings}
        />

        <RailTable analysis={analysis} onOpenDetail={() => setShowRails(true)} />

        <Card padding="lg">
          <SectionHeader
            title="Results"
            description="Whether the pipeline is converting, and where it loses the most leads."
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(true)}
              >
                History
              </Button>
            }
          />
          {!leads ? (
            <div style={{ marginTop: "var(--space-4)" }}>
              <Skeleton width="100%" height={140} />
            </div>
          ) : all.length === 0 ? (
            <p style={{ color: "var(--color-text-secondary)" }}>No leads yet.</p>
          ) : (
            <>
              <div style={{ marginTop: "var(--space-4)" }}>
                <StatGrid>
                  <StatCard
                    label="Reply rate"
                    value={replyRate}
                    hint={`${f.replied} replied of ${f.contacted} contacted`}
                  />
                  <StatCard
                    label="Win rate"
                    value={winRate}
                    hint={`${f.won} won of ${closed} closed`}
                  />
                  <StatCard
                    label="New this week"
                    value={f.newThisWeek}
                    hint="Leads added, last 7 days"
                  />
                  <StatCard
                    label="Contacted this week"
                    value={f.contactedThisWeek}
                    hint="Touches logged, last 7 days"
                  />
                </StatGrid>
              </div>
              <div style={{ marginTop: "var(--space-5)" }}>
                <Funnel steps={f.steps} />
              </div>
              {hops.length > 0 && (
                <HopRow>
                  {hops.map((hop) => (
                    <Hop key={hop.label}>
                      {hop.label}
                      <HopValue>{hop.rate}</HopValue>
                    </Hop>
                  ))}
                </HopRow>
              )}
              {bottleneck && (
                <Callout $tone="warning">
                  <CalloutLabel>Biggest drop-off</CalloutLabel>
                  <CalloutValue>
                    {bottleneck.retained.toFixed(1)}% retained
                  </CalloutValue>
                  <CalloutHint>
                    {bottleneck.from} to {bottleneck.to} is where you lose the
                    most leads. Focus follow-ups there.
                  </CalloutHint>
                </Callout>
              )}
            </>
          )}
        </Card>
      </SectionSpacing>
    );
  };

  // ── Leads (the follower: all leads + their state) ─────────────────────────
  const filtersActive = channelFilter !== "all" || search.trim().length > 0;
  const renderLeads = () => (
    <SectionSpacing>
      <Card padding="md">
        <FilterRow style={{ marginBottom: 0 }}>
          <ChannelFilter
            channels={queue.channels}
            value={channelFilter}
            onChange={setChannelFilter}
          />
          <div style={{ minWidth: 200, flex: 1 }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, niche…"
            />
          </div>
          {filtersActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setChannelFilter("all");
                setSearch("");
              }}
            >
              Clear
            </Button>
          )}
          <Tag variant="neutral" size="sm">
            {leads ? `${filteredLeads.length} of ${all.length}` : "Loading"}
          </Tag>
        </FilterRow>
      </Card>

      {!leads ? (
        <Card padding="lg">
          <Skeleton width="30%" height={16} />
          <div style={{ marginTop: "var(--space-4)" }}>
            <Skeleton width="100%" height={260} />
          </div>
        </Card>
      ) : filteredLeads.length === 0 ? (
        <Card padding="lg">
          <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
            No leads match these filters.
          </p>
        </Card>
      ) : (
        <LeadBoard
          leads={filteredLeads}
          today={today}
          model={model}
          onSelect={setSelected}
          onMoveStage={moveLeadStage}
        />
      )}
    </SectionSpacing>
  );

  // ── states ────────────────────────────────────────────────────────────────
  // Only the account list blocks the screen. The leads do NOT: the default view
  // is the verdict, the findings, the drafts and the rails, none of which come
  // from the lead list, and waiting on 1,500 lead documents to paint a verdict
  // that is already in hand is the whole reason this screen felt slow. The
  // follow-up tier and the results card show their own skeletons instead.
  if (accounts === null) {
    return (
      <>
        {toolbar}
        <SectionSpacing>
          <StatGrid>
            {[0, 1, 2, 3].map((i) => (
              <Card key={i} padding="md">
                <Skeleton width="50%" height={14} />
                <div style={{ marginTop: "var(--space-3)" }}>
                  <Skeleton width="40%" height={28} />
                </div>
              </Card>
            ))}
          </StatGrid>
          <Card padding="lg">
            <Skeleton width="35%" height={18} />
            <div style={{ marginTop: "var(--space-4)" }}>
              <Skeleton width="100%" height={120} />
            </div>
          </Card>
        </SectionSpacing>
      </>
    );
  }

  if (error) {
    return (
      <>
        {toolbar}
        <Card padding="lg">
          <Tag variant="danger" withDot>
            Error
          </Tag>
          <p
            style={{
              marginTop: "var(--space-3)",
              color: "var(--color-text-secondary)",
            }}
          >
            {error}
          </p>
          <div style={{ marginTop: "var(--space-4)" }}>
            <Button variant="secondary" size="sm" onClick={load}>
              Try again
            </Button>
          </div>
        </Card>
      </>
    );
  }

  if (accounts.length === 0) {
    return (
      <>
        {toolbar}
        <EmptyState
          icon={<IconCompass size={20} />}
          title="No outreach boards yet"
          description="No community has an outreach board yet. Leads are added through Claude; the first lead creates the community's board."
        />
      </>
    );
  }

  if (!accountId) {
    return (
      <>
        {toolbar}
        <EmptyState
          icon={<IconCompass size={20} />}
          title="Pick a community"
          description="Each community has its own outreach board and pipeline. Choose one from the selector above to see its leads."
        />
      </>
    );
  }

  // Drafts can exist before any lead does (the daily run queues messages for
  // review first), so the empty state only takes over when both are empty and
  // the leads have actually arrived. leads === null means "not here yet", which
  // is not the same as "none", and the screen no longer blocks on that fetch.
  if (leads !== null && all.length === 0 && drafts.length === 0) {
    return (
      <>
        {toolbar}
        <EmptyState
          icon={<IconCompass size={20} />}
          title="No leads yet"
          description="Leads are added and updated through Claude. Once leads are added, today's follow-ups and the pipeline show up here."
        />
        {stageEditor}
      </>
    );
  }

  return (
    <>
      {toolbar}
      <TabBar role="tablist">
        {TABS.map((t) => {
          const badge = tabBadges[t.id];
          return (
            <TabButton
              key={t.id}
              type="button"
              role="tab"
              $active={tab === t.id}
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
            >
              {t.icon}
              {t.label}
              {badge && <TabBadge $tone={badge.tone}>{badge.count}</TabBadge>}
            </TabButton>
          );
        })}
      </TabBar>

      {quickError && (
        <div style={{ marginBottom: "var(--space-4)" }}>
          <Banner role="alert">
            <span>{quickError}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setQuickError(null)}
            >
              Dismiss
            </Button>
          </Banner>
        </div>
      )}

      {tab === TAB_TODAY && renderToday()}
      {tab === TAB_LEADS && renderLeads()}

      {showDrafts && (
        <Modal
          title="Drafts"
          subtitle="Approve, edit or reject what the run wrote."
          wide
          onClose={() => setShowDrafts(false)}
        >
          <ReviewQueue
            drafts={drafts}
            lastRun={lastRun}
            accountId={accountId}
            onUpdated={applyDraftUpdate}
          />
        </Modal>
      )}

      {showRails && (
        <RailDetail
          accountId={accountId}
          snapshots={analysis.snapshots}
          leads={all}
          throughput={throughput}
          escapeDisabled={!!openWorker || !!selected}
          onSelectLead={setSelected}
          onClose={() => setShowRails(false)}
        />
      )}

      {showHistory && (
        <HistoryPanel
          analysis={analysis}
          queue={queue}
          onClose={() => setShowHistory(false)}
        />
      )}

      {selected && (
        <LeadDetail
          lead={selected}
          today={today}
          accountId={accountId}
          model={model}
          onClose={() => setSelected(null)}
          onUpdated={applyLeadUpdate}
        />
      )}

      {openWorker && (
        <WorkerModal
          worker={openWorker}
          leads={all}
          drafts={drafts}
          model={model}
          today={today}
          ops={ops}
          onClose={() => setOpenWorker(null)}
          onNavigate={(id) => {
            setOpenWorker(null);
            setShowRails(false);
            // 3 was the Review tab. Drafts are a surface now, not a tab.
            if (id === 3) {
              setShowDrafts(true);
              return;
            }
            setTab(id === TAB_LEADS ? TAB_LEADS : TAB_TODAY);
          }}
          onSelectLead={(l) => {
            setOpenWorker(null);
            setSelected(l);
          }}
        />
      )}

      {stageEditor}
    </>
  );
};

export default OutreachDashboard;
export { OutreachDashboard };
